import { Button, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useTreatmentServiceGetEpidemiologyCaseListEndpoint } from 'src/api/queries'
import { EpidemiologyCasePagedListItem } from 'src/api/requests'
import { SearchBar } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import { StatusTag } from '../components'

export const EpidemiologyCasesTab = () => {
  const { t } = useLocales()

  const { id } = useParams()
  const navigate = useNavigate()

  const pagination = useTablePagination()

  const [search, setSearch] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    patientId: id,
    ...(search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useTreatmentServiceGetEpidemiologyCaseListEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const columns: ColumnsType<EpidemiologyCasePagedListItem> = [
    {
      key: 'epidemiology number',
      title: t('epidemiology.epidemiology_number'),
      render: (_, { code }) => code || '-'
    },
    {
      key: 'created at',
      title: t('investigations.created_date'),
      render: (_, { createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'sime code',
      title: t('common.sime_code'),
      render: (_, { patient }) => patient?.simeCode
    },
    {
      key: 'tarv code',
      title: t('common.tarv_code'),
      render: (_, { patient }) => patient?.tarvCode
    },
    {
      key: 'sbtesp code',
      title: t('epidemiology.sbtesp_code'),
      render: (_, { number }) => number || '-'
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => status && <StatusTag status={status} />
    },
    {
      key: 'details',
      render: ({ id: caseId }) => (
        <Button
          type="link"
          onClick={() =>
            navigate(
              paths.patients.epidemiologyCases.details(String(id), caseId)
            )
          }
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      <SearchBar
        maxWidth={300}
        onSearch={onSearch}
        placeholder={t('epidemiology.search_placeholder')}
      />

      <Table
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
